/**
 * Password Helper Utility
 *
 * Provides secure password hashing and verification for student accounts.
 * Uses bcryptjs for industry-standard password security.
 *
 * @example
 * // Hash a password
 * const hashedPassword = await hashPassword('ABC1234xy');
 *
 * // Verify a password
 * const isValid = await verifyPassword('ABC1234xy', hashedPassword);
 */

import bcrypt from 'bcryptjs';

/**
 * Number of salt rounds for bcrypt hashing
 * Higher = more secure but slower
 * 10 rounds is a good balance for most applications
 */
const SALT_ROUNDS = 10;

/**
 * Hash a password using bcrypt
 *
 * @param password - Plain text password to hash
 * @returns Promise that resolves to the hashed password
 *
 * @example
 * const hashed = await hashPassword('ABC1234xy');
 * // Returns: $2a$10$...hashed_password...
 */
export async function hashPassword(password: string): Promise<string> {
  try {
    // Generate salt and hash password in one step
    const hashedPassword = await bcrypt.hash(password, SALT_ROUNDS);
    return hashedPassword;
  } catch (error) {
    console.error('Error hashing password:', error);
    throw new Error('Failed to hash password');
  }
}

/**
 * Verify a password against a hashed password
 *
 * @param password - Plain text password to verify
 * @param hashedPassword - Hashed password to compare against
 * @returns Promise that resolves to true if password matches, false otherwise
 *
 * @example
 * const isValid = await verifyPassword('ABC1234xy', hashedPassword);
 * if (isValid) {
 *   console.log('Password is correct!');
 * }
 */
export async function verifyPassword(
  password: string,
  hashedPassword: string
): Promise<boolean> {
  try {
    // Compare password with hashed password
    const isMatch = await bcrypt.compare(password, hashedPassword);
    return isMatch;
  } catch (error) {
    console.error('Error verifying password:', error);
    return false;
  }
}

/**
 * Check if a string is already a bcrypt hash
 *
 * @param str - String to check
 * @returns True if string appears to be a bcrypt hash
 *
 * @example
 * isHashedPassword('ABC1234xy'); // false
 * isHashedPassword('$2a$10$...'); // true
 */
export function isHashedPassword(str: string): boolean {
  // Bcrypt hashes start with $2a$, $2b$, or $2y$ followed by cost factor
  return /^\$2[aby]\$\d{2}\$.{53}$/.test(str);
}

/**
 * Migrate plain text password to hashed password
 * Useful for one-time migration of existing data
 *
 * @param password - Password that may be plain text or already hashed
 * @returns Promise that resolves to hashed password
 *
 * @example
 * // If already hashed, returns as-is
 * const hash1 = await migratePassword('$2a$10$...');
 * // If plain text, hashes it
 * const hash2 = await migratePassword('ABC1234xy');
 */
export async function migratePassword(password: string): Promise<string> {
  // If already hashed, return as-is
  if (isHashedPassword(password)) {
    return password;
  }

  // Otherwise, hash it
  return hashPassword(password);
}
